import { inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CONFIG_TOKEN, DialogRef, LIGHTBOX_TOKEN, MediaLightboxDialogLike, MediaLightboxItem } from '@pxl/models/common';
import { DialogService, LAZY_DIALOG_REGISTRY, LoadingService, LoginService, MediaViewerService, ThemeService } from '@pxl/models/contracts';
import { DocumentModel, DocumentService, DocumentType } from '@pxl/shared/generated';
import { extensionToMime, MimeType } from '@pxl/shared/util';
import { firstValueFrom, forkJoin, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Injectable()
export class DefaultMediaViewerService implements MediaViewerService {
    private readonly domSanitizer = inject(DomSanitizer);
    private readonly loadingService = inject(LoadingService);
    private readonly documentBackendService = inject(DocumentService);
    private readonly themeService = inject(ThemeService);
    private readonly dialogService = inject(DialogService);
    private readonly loginService = inject(LoginService);
    private readonly config = inject(CONFIG_TOKEN);
    private readonly lazyDialogs = inject(LAZY_DIALOG_REGISTRY);
    private readonly mediaLightboxComponent = inject(LIGHTBOX_TOKEN);

    public showMedia(title: string, media: DocumentModel[], startDocumentId: string): void {
        void this.loadingService
            .showWait(
                firstValueFrom(
                    this.documentBackendService.createViewerUrl(
                        media.map((document) => document.id),
                        this.config.API_VERSION,
                        this.loginService.getToken() ?? '',
                        startDocumentId,
                        false,
                        this.themeService.darkSchemePreference,
                    ),
                ),
            )
            .then((uri) => {
                const url = this.domSanitizer.bypassSecurityTrustResourceUrl(uri.url);
                return this.lazyDialogs.MediaViewer().then((MediaViewerDialogComponent) => {
                    this.dialogService.open(MediaViewerDialogComponent, { mediaUrl: url, title });
                });
            });
    }

    /**Lightbox */
    private lightboxDialogRef: DialogRef<MediaLightboxDialogLike> | null = null;
    private lightboxObjectUrls: string[] = [];

    public showMediaInLightbox(media: DocumentModel[], startIndex: number): void {
        if (!media.length) {
            return;
        }

        const mediaItems$ = forkJoin(media.map((document) => this.toMediaLightboxItem(document)));

        this.loadingService
            .showWaitObservable(mediaItems$)
            .pipe(take(1))
            .subscribe((mediaItems) => this.openLightbox(mediaItems, startIndex));
    }

    public showMediaUrlsInLightbox(items: MediaLightboxItem[], startIndex: number): void {
        if (!items.length) {
            return;
        }

        this.openLightbox(items, startIndex);
    }

    /**
     * Downloads a single document and maps it to a lightbox item. The document type
     * (image vs. video) is taken from the backend `documentType`; the download itself
     * only yields raw base64, so the concrete MIME type is derived from the file name.
     */
    private toMediaLightboxItem(document: DocumentModel): Observable<MediaLightboxItem> {
        const isVideo = document.documentType === DocumentType.Video;
        return this.downloadDocument(document.id).pipe(
            map(
                (buffer): MediaLightboxItem => ({
                    type: isVideo ? 'video' : 'image',
                    src: this.createBlobUrl(buffer, this.getMimeType(document)),
                    alt: document.documentName ?? undefined,
                    title: document.documentName ?? undefined,
                }),
            ),
        );
    }

    private downloadDocument(documentId: string): Observable<ArrayBuffer> {
        const token = this.loginService.getToken() ?? '';
        return this.documentBackendService.downloadDocument(documentId, this.config.API_VERSION, token).pipe(map(this.base64ToArrayBuffer));
    }

    private createBlobUrl(buffer: ArrayBuffer, mimeType: string): string {
        const blob = new Blob([buffer], { type: mimeType });
        const objectUrl = URL.createObjectURL(blob);
        this.lightboxObjectUrls.push(objectUrl);
        return objectUrl;
    }

    private getMimeType(document: DocumentModel): string {
        return this.mimeForFile(document.documentName ?? document.image?.uri ?? '', document.documentType === DocumentType.Video);
    }

    private mimeForFile(fileName: string, isVideo: boolean): string {
        const extension = this.getFileExtension(fileName);
        if (isVideo) {
            return extension ? `video/${extension === 'mov' ? 'quicktime' : extension}` : 'video/mp4';
        }
        return extensionToMime(extension) || MimeType.JPEG;
    }

    private getFileExtension(fileName: string): string {
        return fileName.split('?')[0].split('.').pop()?.toLowerCase() ?? '';
    }

    private base64ToArrayBuffer(base64: string): ArrayBuffer {
        const binaryString = atob(base64);
        const length = binaryString.length;
        const bytes = new Uint8Array(length);
        for (let i = 0; i < length; i++) {
            bytes[i] = binaryString.codePointAt(i);
        }
        return bytes.buffer;
    }

    private openLightbox(mediaItems: MediaLightboxItem[], startIndex: number, title?: string): void {
        this.closeLightbox();

        const dialogRef = this.dialogService.open(this.mediaLightboxComponent, { mediaItems, startIndex, title });
        this.lightboxDialogRef = dialogRef;

        dialogRef.instance.closed.pipe(take(1)).subscribe(() => {
            this.lightboxDialogRef = null;
            this.revokeObjectUrls();
        });
    }

    private revokeObjectUrls(): void {
        this.lightboxObjectUrls.forEach((url) => URL.revokeObjectURL(url));
        this.lightboxObjectUrls = [];
    }

    public closeLightbox(): void {
        this.lightboxDialogRef?.instance.close();
    }
}
